import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {toast} from 'react-toastify';

import {Gear} from '../../icons';
import {Switch} from '../../buttons/Swtich';
import {Spinner} from '../../Spinner';
import {AnchoredPopover} from '../../elements/AnchoredPopover';
import {DashboardColumnColorService} from '../../../utils/DashboardColumnColorService';
import {isOk} from '../../../utils/apiResponse';
import {useAlertRulesAccess} from '../../../context/UserModulesContext';
import {ALERTS_RULES, PARAM_GEAR, systemRulePath} from '../../alerts_center/alertsCenterPaths';
import {ThresholdFields} from '../../alerts_center/shared/ThresholdFields';
import {
    columnLevelsPayload,
    columnRule,
    initialThresholdValues,
    thresholdFields,
    thresholdLabelKey,
    thresholdsChanged,
    UNIT_DAYS,
    validateThresholds,
} from '../../alerts_center/systemRulesModel';

// Adónde llevan «Cambiar los números» y «Prender la alerta»: con el Centro de Alertas, a la fila de
// ESTA regla en «Mis reglas» (resaltada y con su panel abierto); sin él, a la pantalla de siempre.
const CLASSIC_ALERTS_PATH = '/agency/alerts-configuration';

const LEVEL_COLORS = {
    CRITIC: 'critic',
    BAD: 'bad',
    NORMAL: 'normal',
    GOOD: 'good',
    SUCCESS: 'success',
};

/** ¿La URL pide abrir la tuerca de esta columna? (`?tuerca=<col>`, el link de una alerta que sólo pinta). */
function requestedByUrl(columnId) {
    if (typeof window === 'undefined' || !window.location) return false;
    return new URLSearchParams(window.location.search).get(PARAM_GEAR) === columnId;
}

// Se atiende una vez: sin el parámetro, un remontaje del encabezado no la vuelve a abrir.
function forgetUrlRequest() {
    const params = new URLSearchParams(window.location.search);
    params.delete(PARAM_GEAR);
    const query = params.toString();
    const url = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`;
    if (window.history && window.history.replaceState) window.history.replaceState(window.history.state, '', url);
}

// Las columnas cuya tuerca está montada ahora: `?tuerca=<col>` de una columna que no está en el Tablero no tiene a
// quién abrir. El primer encabezado que monta espera un momento a que monten todos y, si nadie la reclamó, lo dice
// (review C-av2-fe-centro-15).
const mountedGearColumns = new Set();
let gearRequestCheck = null;
export const GEAR_REQUEST_GRACE_MS = 1500;

function scheduleGearRequestCheck(message) {
    if (gearRequestCheck) return;
    gearRequestCheck = setTimeout(() => {
        gearRequestCheck = null;
        const wanted = new URLSearchParams(window.location.search).get(PARAM_GEAR);
        if (!wanted || mountedGearColumns.has(wanted)) return;
        forgetUrlRequest();
        toast.info(message, {toastId: 'csp-hidden-column'});
    }, GEAR_REQUEST_GRACE_MS);
}

// El nivel que marca un 422 tipado del PUT (`INVALID_VALUE`, con su `field`).
function levelOfField(field, fields) {
    const text = String(field || '').toUpperCase();
    const found = fields.find((item) => text.includes(item.level));
    return found ? found.level : (fields[0] ? fields[0].level : null);
}

/**
 * La tuerca del encabezado de una columna del Tablero: su semáforo.
 *
 * - En una agencia con las alertas CONVERTIDAS (Centro de Alertas v2, README v2 §4.4) el color y el aviso
 *   son dos números distintos, así que acá se editan Malo y Crítico (o uno solo, o días) con los mismos
 *   campos y la misma validación que el panel de las de Dashbo (`ThresholdFields`), y Guardar escribe sólo
 *   la sensibilidad de esta columna (`PUT …/dashboard-column-color/<col>/ {levels}`). El pie lo dice: esto
 *   pinta, no envía; los correos y el webhook se configuran en Alertas, cada uno con su número. Con el
 *   semáforo apagado de raíz, «Prenderlo» lo prende ahí mismo.
 * - Sin convertir, el número del color ES el del aviso: los umbrales se leen y se cambian en la pantalla de
 *   Alertas, adonde lleva el link del pie. Como siempre.
 *
 * Va sobre `AnchoredPopover` (portal a `body`: el `<th>` es sticky y su contenedor tiene `overflow:auto`):
 * no se cierra con el scroll de adentro ni con el teclado del celular, y se cierra con Escape, un clic o
 * el foco afuera. `?tuerca=<col>` la abre al montar.
 */
export function ColumnSensitivityPopover({columnId, onColorChanged}) {
    const {t, i18n} = useTranslation();
    const locale = (i18n && i18n.language) || 'es';
    const {enabled: alertsCenterEnabled, converted: agencyConverted} = useAlertRulesAccess();
    const [isOpen, setIsOpen] = useState(false);
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [values, setValues] = useState({});
    const [serverErrors, setServerErrors] = useState({});
    const [saveError, setSaveError] = useState(null);
    const [saved, setSaved] = useState(false);
    const buttonRef = useRef(null);
    const mounted = useRef(true);
    const titleId = `csp-title-${String(columnId).replace(/[^A-Za-z0-9_-]/g, '_')}`;
    const colorLabelId = `${titleId}-color`;

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    // El GET también puede decirlo (lo arma el frente del envío): cualquiera de los dos alcanza.
    const editable = Boolean(data) && (agencyConverted || data.system_alerts_converted === true);
    const rule = useMemo(() => (data ? columnRule(data) : null), [data]);
    const fields = useMemo(() => (rule ? thresholdFields(rule, {locale}) : []), [rule, locale]);

    const resetValues = useCallback((next) => {
        setValues(next ? initialThresholdValues(columnRule(next), {locale}) : {});
        setServerErrors({});
        setSaveError(null);
    }, [locale]);

    const load = useCallback(async () => {
        setIsLoading(true);
        setHasError(false);
        try {
            const next = await DashboardColumnColorService.load(columnId);
            if (!mounted.current) return;
            setData(next);
            resetValues(next);
        } catch (error) {
            if (mounted.current) setHasError(true);
        } finally {
            if (mounted.current) setIsLoading(false);
        }
    }, [columnId, resetValues]);

    const open = useCallback(() => {
        setIsOpen(true);
        setSaved(false);
        load();
    }, [load]);

    const close = useCallback(({returnFocus = false} = {}) => {
        setIsOpen(false);
        if (returnFocus && buttonRef.current) buttonRef.current.focus();
    }, []);

    useEffect(() => {
        mountedGearColumns.add(columnId);
        if (requestedByUrl(columnId)) {
            forgetUrlRequest();
            open();
        } else if (new URLSearchParams(window.location.search).get(PARAM_GEAR)) {
            scheduleGearRequestCheck(t('column_sensitivity.hidden_column'));
        }
        return () => { mountedGearColumns.delete(columnId); };
        // Sólo al montar: es el pedido del link.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleOpen = (event) => {
        // El `<th>` entero ordena la tabla al click: sin esto, abrir la tuerca reordena.
        event.preventDefault();
        event.stopPropagation();
        if (isOpen) close();
        else open();
    };

    const handleShowsColorChange = async (showsColor) => {
        setIsSaving(true);
        try {
            setData(await DashboardColumnColorService.setShowsColor(columnId, showsColor));
            // El color de cada celda vive en el payload de su fila, no acá: sin avisarle al
            // Tablero que lo vuelva a pedir, el switch se mueve y la tabla no cambia nada.
            if (onColorChanged) {
                onColorChanged();
            }
        } catch (error) {
            setHasError(true);
        } finally {
            setIsSaving(false);
        }
    };

    const localErrors = rule && editable ? validateThresholds(rule, values, {locale}) : {};
    const errors = {...localErrors, ...serverErrors};
    const hasErrors = Object.keys(errors).length > 0;
    const changed = Boolean(rule) && thresholdsChanged(rule, values, {locale});

    const setValue = (level, value) => {
        setValues((current) => ({...current, [level]: value}));
        setServerErrors({});
        setSaveError(null);
        setSaved(false);
    };

    const saveLevels = async (event) => {
        event.preventDefault();
        if (!editable || hasErrors || !changed || isSaving) return;
        setIsSaving(true);
        const response = await DashboardColumnColorService.saveColumnLevels(columnId, columnLevelsPayload(rule, values, {locale}));
        if (!mounted.current) return;
        setIsSaving(false);
        if (!isOk(response)) {
            const body = (response && response.raw_body) || {};
            const code = (response && (response.error_code || response.status)) || body.error_code;
            if (code === 'INVALID_VALUE') {
                const level = levelOfField((response && response.field) || body.field, fields);
                const days = fields[0] && fields[0].unit === UNIT_DAYS;
                if (level) {
                    setServerErrors({[level]: days ? 'alerts_center.rules.validation_order_days' : 'alerts_center.rules.validation_order'});
                    return;
                }
            }
            setSaveError(code === 'ERROR_USER_DOES_NOT_HAVE_ACCESS'
                ? 'alerts_center.no_edit_permission' : 'column_sensitivity.save_error');
            return;
        }
        const next = response.result && Array.isArray(response.result.levels) ? response.result : null;
        if (next) {
            setData(next);
            resetValues(next);
        } else {
            // Sin el GET de vuelta, lo guardado es lo que se escribió.
            setData((current) => ({...current, levels: columnLevelsPayload(rule, values, {locale})
                .map((item) => ({level: item.level, value: item.value === null ? null : item.value / 100}))}));
        }
        setSaved(true);
        if (onColorChanged) onColorChanged();
    };

    const turnAlertOn = async () => {
        setIsSaving(true);
        const response = await DashboardColumnColorService.setAlertActive(data.indicator, true);
        if (!mounted.current) return;
        setIsSaving(false);
        if (!isOk(response)) {
            setSaveError('column_sensitivity.turn_on_error');
            return;
        }
        await load();
        if (onColorChanged) onColorChanged();
    };

    const formattedValue = (value) => {
        if (value === null || value === undefined) {
            return t('column_sensitivity.no_value');
        }
        // Los umbrales de desvío se guardan como fracción (0,2 = 20%), igual que en la
        // pantalla de Alertas, que también multiplica al mostrarlos. Los días también: el motor
        // compara `valor × 100` contra los días que quedan (`FundsAlert.levels`).
        const shown = Math.round(value * 100);
        return t(`column_sensitivity.value.${data.unit}`, {value: shown});
    };

    // `|| []` no es paranoia gratuita: el popover se monta DENTRO de la tabla, así que un
    // `result` sin `levels` —un backend viejo, una respuesta a medias— tiraría el error hasta
    // el ErrorBoundary global y el usuario vería "Algo salió mal" en lugar del Tablero entero.
    const renderLevels = () => (data.levels || []).map((level) => (
        <div className="csp-level" key={level.level}>
            <span className={`csp-level__dot -${LEVEL_COLORS[level.level] || 'normal'}`}/>
            <span className="csp-level__label">{t(`column_sensitivity.level.${level.level}`)}</span>
            <span className="csp-level__value">{formattedValue(level.value)}</span>
        </div>
    ));

    const renderToggle = (alertIsOff) => (
        <div className="csp-toggle">
            <span className="csp-toggle__label" id={colorLabelId}>{t('column_sensitivity.shows_color')}</span>
            <Switch name={`shows-color-${columnId}`}
                    checked={data.shows_color}
                    disabled={isSaving || alertIsOff}
                    aria-labelledby={colorLabelId}
                    onChange={(event) => handleShowsColorChange(event.target.checked)}/>
        </div>
    );

    const renderOverrides = () => (data.business_unit_overrides_count > 0 && (
        <p className="csp-note">
            {t('column_sensitivity.business_unit_overrides',
                {count: data.business_unit_overrides_count})}
        </p>
    ));

    // Sin convertir: el número del color es el del aviso, y se cambia en Alertas.
    const renderReadOnly = () => {
        // Con la alerta apagada el motor ni evalúa la columna, así que el color no puede hacer
        // nada. El aviso va PRIMERO y con el CTA que resuelve: enterrado entre los umbrales se
        // lee como una nota al pie, y quien lo leyó igual movió el toggle esperando un efecto.
        const alertIsOff = !data.alert_is_active;
        const alertsPath = alertsCenterEnabled ? systemRulePath(data.indicator) : CLASSIC_ALERTS_PATH;

        return (
            <>
                {alertIsOff && (
                    <div className="csp-alert-off">
                        <strong>{t('column_sensitivity.alert_is_off_title')}</strong>
                        <p>{t('column_sensitivity.alert_is_off')}</p>
                        <Link className="csp-link" to={alertsPath}>
                            {t('column_sensitivity.turn_alert_on')}
                        </Link>
                    </div>
                )}

                <div className={`csp-levels${alertIsOff ? ' -muted' : ''}`}>{renderLevels()}</div>

                {renderOverrides()}

                {renderToggle(alertIsOff)}

                {alertIsOff && (
                    <p className="csp-note">{t('column_sensitivity.shows_color_disabled')}</p>
                )}

                <div className="csp-foot">
                    <strong>{t('column_sensitivity.paints_only')}</strong>
                    {/* Con el Centro, «Mis alertas» no es una pestaña: es el link de arriba de «Mis
                        reglas» (recorrido con Pablo, 13/10). En el clásico sí es la pestaña del usuario. */}
                    <p>{t(alertsCenterEnabled
                        ? 'column_sensitivity.paints_only_explanation_center'
                        : 'column_sensitivity.paints_only_explanation')}</p>
                    {/* `Link` y no `<a href>`: el panel va por portal pero sigue dentro del
                        Router, y un href recarga la app entera (1,35 MB de bundle). */}
                    <Link className="csp-link" to={alertsPath}>
                        {t('column_sensitivity.change_numbers')}
                    </Link>
                </div>
            </>
        );
    };

    // Convertida: el semáforo se edita acá, y no envía nada.
    const renderEditable = () => {
        const alertIsOff = !data.alert_is_active;
        return (
            <form className="csp-form" onSubmit={saveLevels} noValidate data-testid="csp-editable">
                {alertIsOff && (
                    <div className="csp-alert-off" data-testid="csp-off">
                        <strong>{t('column_sensitivity.semaphore_off_title')}</strong>
                        <p>{t('column_sensitivity.semaphore_off')}</p>
                        <button type="button" className="alc-btn -ghost csp-turn-on" disabled={isSaving} onClick={turnAlertOn}
                                data-testid="csp-turn-on">
                            {t('column_sensitivity.turn_on')}
                        </button>
                    </div>
                )}

                <div className={`csp-thresholds${alertIsOff ? ' -muted' : ''}`}>
                    <ThresholdFields fields={fields} values={values} errors={errors} idPrefix={`csp-${titleId}`}
                                     labelFor={(level) => t(thresholdLabelKey(rule, level))} onChange={setValue}/>
                </div>

                {renderOverrides()}

                {renderToggle(alertIsOff)}

                <div className="csp-foot">
                    <strong>{t('column_sensitivity.paints_only')}</strong>
                    <p>{t('column_sensitivity.paints_only_explanation_v2')}</p>
                    <Link className="csp-link" to={ALERTS_RULES}>{t('column_sensitivity.go_to_alerts')}</Link>
                </div>

                {saveError && <p className="csp-error" role="alert">{t(saveError)}</p>}
                {saved && !changed && <p className="csp-saved" role="status">{t('column_sensitivity.saved')}</p>}

                <div className="csp-actions">
                    <button type="button" className="alc-btn -ghost" disabled={isSaving}
                            onClick={() => (changed ? resetValues(data) : close({returnFocus: true}))}>
                        {t('column_sensitivity.cancel')}
                    </button>
                    <button type="submit" className="alc-btn -primary" disabled={!changed || hasErrors || isSaving}
                            data-testid="csp-save">
                        {isSaving ? t('column_sensitivity.saving') : t('column_sensitivity.save')}
                    </button>
                </div>
            </form>
        );
    };

    const renderBody = () => {
        if (isLoading) {
            return <div className="csp-loader"><Spinner color="positive small"/></div>;
        }

        if (hasError || !data) {
            return <p className="csp-error">{t('column_sensitivity.load_error')}</p>;
        }

        return editable ? renderEditable() : renderReadOnly();
    };

    return (
        <>
            <button type="button"
                    className={`csp-button${isOpen ? ' -open' : ''}`}
                    ref={buttonRef}
                    onClick={toggleOpen}
                    aria-expanded={isOpen}
                    aria-label={t('column_sensitivity.title')}>
                <Gear size={14} weight="bold"/>
            </button>
            {isOpen && (
                <AnchoredPopover anchorRef={buttonRef} onClose={close} labelledBy={titleId} baseClassName="csp-panel"
                                 className={editable ? '-editable' : ''} testId="csp-panel">
                    <div className="csp-head" id={titleId}>
                        <Gear size={16} weight="bold"/>
                        <span>{t('column_sensitivity.title')}</span>
                    </div>
                    {renderBody()}
                </AnchoredPopover>
            )}
        </>
    );
}
